// composables/useSupplierDailyOrders.js
// Box-first data layer for the Suppliers-Club Daily Orders feature (box-first
// design spec 2026-07-07 Section 6/Section 7). TWO surfaces share this file — they REPLACE the
// earlier flat-list fan-out-on-mount (which enumerated every box then fetched
// EVERY box's detail on list mount and flattened — the exact N+1 this split kills):
//
//   • useSupplierBoxList()        — the "fetchBoxList surface". ONE sparse,
//     cursor-paginated GET /supplier-boxes → SupplierBox ORM rows. NO items[].
//     A few hundred bytes per card. Consumed by the اوردرات اليوم tab
//     (SupplierDailyOrdersPage — the card list, P4-B). Mirrors useSupplierWallPosts'
//     cursor / de-dup / stale-400-head-restart / dev-fixture pattern verbatim.
//
//   • useSupplierBoxDetail(boxId) — the "fetchBoxDetail surface". ONE lazy per-box
//     GET /supplier-boxes/{id}?include=items,items.notes,invoice → items + the
//     advisory writes (reserve / markOos / clear) + the SINGLE-box IN_COLLECTION
//     poll. Called LAZILY only when a card is tapped, never on list mount.
//     Consumed by the detail route (SupplierBoxDetailPage, P4-A).
//
// PRIVACY INVARIANT (locked, brief L4): buyer/member identity is NEVER read or
// exposed. `memberNote` carries the ordering member's note with no identity.
//
// SINGLE-INSTANCE OWNERSHIP: each surface is called ONCE by its owning page. The
// detail surface owns a poll timer + a visibilitychange listener that auto-dispose
// when the owning page's effect scope tears down (onScopeDispose).
import { useRepo } from 'pinia-orm';
import { computed, getCurrentScope, onScopeDispose, ref, toValue, watch } from 'vue';
import { useToast } from '@/composables/useToast.js';
import {
  deriveFrozenReason,
  frozenReasonI18nKey,
  isOosSignal,
  isReservedSignal,
  SupplierBoxState,
  SupplierSignal,
} from '@/enums/commerce/SupplierSignal.enum.js';
import { jsonApiClient } from '@/httpConfig/config/axios.js';
import i18n from '@/i18n';
import SupplierBox from '@/models/commerce/SupplierBox.js';
import SupplierDailyOrderItem from '@/models/commerce/SupplierDailyOrderItem.js';
import {
  clearSignal as clearSignalRequest,
  markOos as markOosRequest,
  reserveItem as reserveItemRequest,
  SUPPLIER_WRITE_FROZEN,
} from '@/services/supplierDailyOrders.service.js';

// The box index and detail reads are exported for the offline preview's recorder
// (collector/previewReplay.js), which saves the first pages a showroom shows.
export const BOXES_ENDPOINT = '/supplier-boxes';

// Cursor page size for the box index. Boxes are per-(supplier × plan) and rarely
// overlapping, so a supplier realistically has a handful; 20 mirrors the wall's
// PAGE_LIMIT and keeps the first paint cheap. `page[after]` follows for the rare
// long tail.
export const BOX_PAGE_LIMIT = 20;

// Sparse fieldset (design spec Section 6) — the card must answer "how much will I be
// paid" + "is anything out of stock" WITHOUT the nested items[]. invoiceableCents/
// oosCents/planRef confirmed LIVE 2026-07-08 (BE brief 2026-07-08-supplier-boxes-
// index-fields-delivered.md) — no client-side fallback needed.
export const BOX_FIELDS =
  'collectionDate,lifecycleState,itemsTotal,itemsReserved,itemsOosFlagged,itemsCollected,hasInvoice,invoiceableCents,oosCents,planRef,membersServed';

/** The day detail's include. */
export const BOX_DETAIL_INCLUDE = 'items,items.notes,invoice';

/**
 * A showroom supplier sees only days the club bought something on: a finished
 * day with nothing bought is hidden for him (owner, 19 Oct). Shared by the
 * «أيامك» page and the offline preview's recorder.
 */
export function isShownToShowroom(box) {
  return (
    !(Number(box?.lifecycleState) >= SupplierBoxState.RECEIVED) ||
    Number(box?.itemsCollected) > 0 ||
    Number(box?.invoiceableCents) > 0
  );
}

// SupplierBoxState.IN_COLLECTION (int 2) — the ONLY lifecycle state that arms the
// detail poll ("watch the collector arrive", design spec Section 7 / FE plan D4).
const BOX_STATE_IN_COLLECTION = SupplierBoxState.IN_COLLECTION;

// 30s backstop poll (mirrors OrderBoxTotalsPage.vue — no Echo/Reverb in this app).
const POLL_INTERVAL_MS = 30_000;

const t = (key, params) => i18n.global.t(key, params ?? {});

// A stale cursor (its anchor box left the index between fetches) comes back as
// HTTP 400 — same 400 family as a fabricated cursor. Recover by restarting from
// the head (mirror useSupplierWallPosts.isStaleCursor).
function isStaleCursor(err) {
  return err?.response?.status === 400;
}

// ── Day-label derivation (tz-agnostic; owner-locked Western digits) ────────────
function localYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Derive the day label for a box's collectionDate. "Today" → the plain label;
 * any other day → the dated label with a Western-digit D/M. Compared as local
 * Y-M-D strings so a UTC/local offset never mislabels "today". Attached onto each
 * detail item as `dayLabel` for the item card's box-context line.
 */
function dayLabelFor(collectionDate) {
  if (!collectionDate) return t('supplier.dailyOrders.dayLabel');
  // Accept a date-only string ('YYYY-MM-DD', the common JSON:API date shape — its
  // date part is taken verbatim, no tz shift), an ISO datetime, or a Date object.
  let ymd = String(collectionDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const parsed = new Date(collectionDate);
    if (Number.isNaN(parsed.getTime())) return t('supplier.dailyOrders.dayLabel');
    ymd = localYmd(parsed);
  }
  if (ymd === localYmd(new Date())) return t('supplier.dailyOrders.dayLabel');
  const [, mm, dd] = ymd.split('-');
  return t('supplier.dailyOrders.dayLabelDated', { date: `${Number(dd)}/${Number(mm)}` });
}

/**
 * Map a raw box-item into the FE display shape used by the item card.
 *   - Wire field name: BE ships `variantName`; the FE model/card read
 *     `variantLabel`. Map at the read seam so no FE consumer knows the wire name.
 *     `memberNote` rides through `...raw` (same name both sides). PRIVACY: no
 *     identity attached.
 *   - `frozenReason` is DERIVED client-side (the delivered BE slice ships no such
 *     field) from the item `frozen` bool + the box `writable`/`lifecycleState`.
 *     AFTER the spread so it wins over any stray raw value.
 *   - box context (`boxId`/`collectionDate`/`dayLabel`) is attached for grouping,
 *     the item-card box line, and the frozenReason derivation inputs.
 */
function enrichItem(raw, boxCtx) {
  const { boxId, collectionDate, writable, lifecycleState } = boxCtx;
  return {
    ...raw,
    variantLabel: raw.variantLabel ?? raw.variantName ?? null,
    frozenReason: deriveFrozenReason({
      frozen: raw.frozen,
      writable,
      lifecycleState,
    }),
    boxId,
    collectionDate,
    dayLabel: dayLabelFor(collectionDate),
  };
}

// A write response echoes the item but not the client-attached box context.
// pinia-orm `save` merges (unspecified fields are retained), but re-attach
// explicitly so a future full-object save can't drop grouping/poll context.
function reattachBoxContext(updated, existing) {
  return {
    ...updated,
    boxId: updated.boxId ?? existing?.boxId ?? null,
    collectionDate: updated.collectionDate ?? existing?.collectionDate ?? null,
    dayLabel: updated.dayLabel ?? existing?.dayLabel ?? null,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// LIST SURFACE — the sparse box index. ONE cursor-paginated call, NO items[].
// ═══════════════════════════════════════════════════════════════════════════
export function useSupplierBoxList() {
  const repo = useRepo(SupplierBox);

  // Server order is authoritative (cursor-ordered). Keep the id sequence
  // explicitly and derive the rendered list from the ORM so a refresh re-flows
  // through this computed automatically (same contract as useSupplierWallPosts).
  const boxIds = ref([]);
  const nextCursor = ref(null);
  const hasMore = ref(true);
  const loading = ref(false); // first-load / refresh (covers the empty screen)
  const isLoadingMore = ref(false); // pagination (covers the list footer)
  const error = ref(null);
  // "Your shop and the club since {date}" — `meta.history` of the first page
  // ({ since, lines, members, amountCents }); null when the server sends none.
  const history = ref(null);
  let abortController = null;

  const boxes = computed(() => boxIds.value.map((id) => repo.find(id)).filter(Boolean));

  function buildQueryParams(cursor = null) {
    const params = { 'page[limit]': BOX_PAGE_LIMIT, 'fields[supplier-boxes]': BOX_FIELDS };
    if (cursor) params['page[after]'] = cursor;
    return params;
  }

  async function fetchBoxList(reset = false) {
    if (loading.value || (isLoadingMore.value && !reset)) return;

    let needsHeadRestart = false;
    try {
      if (reset) {
        loading.value = true;
        error.value = null;
      } else {
        isLoadingMore.value = true;
      }

      if (abortController) abortController.abort();
      abortController = new AbortController();

      const cursor = reset ? null : nextCursor.value;
      const response = await jsonApiClient.get(BOXES_ENDPOINT, {
        params: buildQueryParams(cursor),
        signal: abortController.signal,
      });

      // jsonApiClient deserialises to { data, meta, links } (see axios.js).
      const data = response?.data?.data ?? [];
      const page = response?.data?.meta?.page ?? {};

      if (reset) {
        repo.flush();
        boxIds.value = [];
        history.value = response?.data?.meta?.history ?? null;
      }

      if (data.length) {
        repo.save(data);
        const seen = new Set(boxIds.value);
        const fresh = data.map((row) => row.id).filter((id) => !seen.has(id)); // de-dupe across overlapping cursors
        boxIds.value = [...boxIds.value, ...fresh];
      }

      nextCursor.value = page.to ?? null;
      hasMore.value = page.hasMore ?? false;
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      // Stale cursor during pagination → restart from the head once this request
      // settles. Flag (don't return — the recovery runs after `finally` clears the
      // in-flight flags); the branches below are reset-only or guarded on the flag.
      if (!reset && isStaleCursor(err)) {
        needsHeadRestart = true;
      }
      // ⚠️ DEV-ONLY (remove when the box index lands live): GET /supplier-boxes
      // isn't guaranteed live yet, so a first load can 404. Seed the box-shaped
      // fixture so the card UI is demonstrable in `npm run dev`. Self-disables
      // once the endpoint returns 200; compiled out of production via the DEV
      // gate. Unit tests reject with a status-less Error, so this never fires there.
      if (import.meta.env.DEV && reset && err?.response?.status === 404) {
        try {
          const { SUPPLIER_DAILY_ORDERS_DEV_BOX_FIXTURE } = await import(
            '@/composables/supplierDailyOrdersDevFixture.js'
          );
          repo.flush();
          repo.save(SUPPLIER_DAILY_ORDERS_DEV_BOX_FIXTURE);
          boxIds.value = SUPPLIER_DAILY_ORDERS_DEV_BOX_FIXTURE.map((b) => b.id);
          hasMore.value = false;
          error.value = null;
          return;
        } catch (fixtureErr) {
          console.error('[useSupplierBoxList] dev fixture load failed:', fixtureErr);
        }
      }
      if (!needsHeadRestart) {
        if (reset) error.value = t('supplier.dailyOrders.boxList.error.description');
        console.error('[useSupplierBoxList] fetch error:', err);
      }
    } finally {
      loading.value = false;
      isLoadingMore.value = false;
      abortController = null;
    }

    // Stale-cursor recovery: flags are cleared, so re-enter cleanly at the head.
    if (needsHeadRestart) {
      nextCursor.value = null;
      hasMore.value = true;
      return fetchBoxList(true);
    }
  }

  function loadInitial() {
    nextCursor.value = null;
    hasMore.value = true;
    return fetchBoxList(true);
  }

  function loadMore() {
    if (!hasMore.value || loading.value || isLoadingMore.value) {
      return Promise.resolve();
    }
    return fetchBoxList(false);
  }

  async function refresh(done) {
    try {
      await loadInitial();
    } finally {
      if (typeof done === 'function') done();
    }
  }

  return {
    boxes,
    history,
    loading,
    isLoadingMore,
    hasMore,
    error,
    loadInitial,
    loadMore,
    refresh,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// DETAIL SURFACE — one box's items + advisory writes + the single-box poll.
// Called LAZILY (on card tap / detail-route mount), never on list mount.
// ═══════════════════════════════════════════════════════════════════════════
export function useSupplierBoxDetail(boxIdInput) {
  const boxRepo = useRepo(SupplierBox);
  const itemRepo = useRepo(SupplierDailyOrderItem);
  const { showToast, showError } = useToast();

  // Accept a plain id, a ref, or a getter (the detail route param), resolved
  // FRESH on each fetch. NOTE (M2): the detail route mounts a fresh instance per
  // box, so `boxId` is effectively call-time-stable — a changing id is NOT
  // auto-refetched here (`box`/`pollArmed` read it reactively and would flip while
  // `items` still shows the old box). If an in-place-swap consumer is ever added,
  // add `watch(resolveId, (id, prev) => { if (id !== prev) loadInitial(); })`.
  const resolveId = () => toValue(boxIdInput);

  // Server order is authoritative; keep the id sequence explicitly and derive the
  // rendered list from the ORM so optimistic writes and silent refetches re-flow
  // through this computed automatically.
  const itemIds = ref([]);
  const loading = ref(false); // first-load / refresh (covers the empty screen)
  const error = ref(null);
  let abortController = null;
  // Non-reactive guard: a silent poll must not clobber an in-flight optimistic
  // write (mirrors OrderBoxTotalsPage's `!confirming` guard).
  let pendingWrites = 0;
  // Per-ITEM re-entrancy guard (H2): a second tap on the SAME item while its
  // write is in flight is ignored, so the optimistic-revert baseline can't be
  // snapshotted from an already-optimistic row and out-of-order responses can't
  // win. `pendingWrites` (the whole-box counter) alone doesn't prevent this.
  const pendingWriteIds = new Set();

  const box = computed(() => {
    const id = resolveId();
    return id != null ? boxRepo.find(id) : null;
  });
  const items = computed(() => itemIds.value.map((id) => itemRepo.find(id)).filter(Boolean));

  // ── Per-box summary derivations (client-derived from item signals + amounts) ──
  const totalCount = computed(() => items.value.length);
  const reservedCount = computed(
    () => items.value.filter((i) => isReservedSignal(i.signal)).length,
  );
  const oosCount = computed(() => items.value.filter((i) => isOosSignal(i.signal)).length);
  const totalAmount = computed(() =>
    items.value.reduce((sum, i) => sum + (Number(i.qty) || 0) * (Number(i.unitPrice) || 0), 0),
  );

  // ── Read: the ONE swap-seam ────────────────────────────────────────────────
  // GET /supplier-boxes/{id}?include=items,items.notes,invoice → the box row +
  // its items in one round-trip. Returns { boxAttrs, items } for the caller to
  // apply; the box-level freeze context (writable / lifecycleState) feeds the
  // per-item client-side frozenReason derivation (the BE slice ships none).
  async function requestBoxDetail(id, { signal }) {
    const resp = await jsonApiClient.get(`${BOXES_ENDPOINT}/${id}`, {
      params: { include: BOX_DETAIL_INCLUDE },
      signal,
    });
    const raw = resp?.data?.data ?? {};
    // Peel the nested relations off before saving the box row (the invoice echo
    // is fetched fresh at tap-time by the invoice sheet — 5-min signed URL).
    const { items: rawItems, invoice: _invoice, ...boxAttrs } = raw;
    const ctx = {
      boxId: id,
      collectionDate: boxAttrs.collectionDate ?? null,
      writable: boxAttrs.writable ?? null,
      lifecycleState: boxAttrs.lifecycleState ?? null,
    };
    const enriched = (Array.isArray(rawItems) ? rawItems : []).map((r) => enrichItem(r, ctx));
    return { boxAttrs: { id, ...boxAttrs }, items: enriched };
  }

  // Apply a settled detail read.
  //   - flush=true  (non-silent load): clear stale item rows first — the spinner
  //     covers the empty flash. Only THIS box's items ever live in the item repo
  //     (the list surface stores SupplierBox rows, never items), so a flush is safe.
  //   - flush=false (silent poll): MERGE only — never blow away an open sheet's
  //     ORM-bound row or an optimistic write. Items removed server-side simply
  //     drop out of `itemIds` (→ unrendered) without being destroyed.
  function applyDetail({ boxAttrs, items: newItems }, { flush }) {
    boxRepo.save(boxAttrs);
    if (flush) itemRepo.flush();
    if (newItems.length) itemRepo.save(newItems);
    itemIds.value = newItems.map((r) => r.id);
  }

  async function fetchDetail({ silent = false } = {}) {
    // Post-teardown short-circuit: a caller that settles AFTER dispose() (the
    // frozen-revert refetch fired from a write still in flight at unmount) must
    // NOT mint a fresh, un-abortable GET that writes a torn-down box into the
    // shared repos. The write itself never flows through `abortController`, so
    // dispose()'s abort can't cancel it — this flag is the only backstop.
    if (disposed) return;
    const id = resolveId();
    if (id == null) return;

    // Guard overlapping non-silent loads; silent polls are allowed through
    // (the per-call AbortController makes latest-wins).
    if (loading.value && !silent) return;

    // Local controller per call — only the shared ref's CURRENT owner may null it
    // (finally). A superseded/aborted call must not null a newer call's controller.
    const controller = new AbortController();
    if (abortController) abortController.abort();
    abortController = controller;

    try {
      if (!silent) {
        loading.value = true;
        error.value = null;
      }

      const result = await requestBoxDetail(id, { signal: controller.signal });

      // Non-clobber, enforced HERE so it covers EVERY caller (poll, visibility,
      // pull-to-refresh, the frozen-revert refetch). Never let a superseded fetch
      // overwrite a newer one, and never blow away in-flight optimistic writes.
      if (abortController !== controller) return;
      if (pendingWrites > 0) return;

      applyDetail(result, { flush: !silent });
      // Clear a prior error on ANY successful fetch — including a silent poll — so
      // a recovered network never strands the page on the retry state.
      error.value = null;
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;

      // Silent (poll / visibility) failure: leave the rendered state untouched.
      if (silent) {
        console.warn('[useSupplierBoxDetail] silent refetch failed', err);
        return;
      }

      // ⚠️ DEV-ONLY (remove when the detail slice lands live): GET
      // /supplier-boxes/{id} may 404 in dev. Seed this box's fixture items so the
      // detail UI is demonstrable in `npm run dev`. Self-disables on 200; compiled
      // out of production; never fires in tests (status-less rejects).
      if (import.meta.env.DEV && err?.response?.status === 404) {
        try {
          const {
            SUPPLIER_DAILY_ORDERS_DEV_BOX_FIXTURE,
            SUPPLIER_DAILY_ORDERS_DEV_BOX_ITEMS_FIXTURE,
          } = await import('@/composables/supplierDailyOrdersDevFixture.js');
          const devBox = SUPPLIER_DAILY_ORDERS_DEV_BOX_FIXTURE.find((b) => b.id === id);
          const devItems = SUPPLIER_DAILY_ORDERS_DEV_BOX_ITEMS_FIXTURE[id] ?? [];
          if (devBox || devItems.length) {
            const ctx = {
              boxId: id,
              collectionDate: devBox?.collectionDate ?? null,
              writable: devBox?.writable ?? null,
              lifecycleState: devBox?.lifecycleState ?? null,
            };
            const enriched = devItems.map((r) => enrichItem(r, ctx));
            if (devBox) boxRepo.save(devBox);
            itemRepo.flush();
            if (enriched.length) itemRepo.save(enriched);
            itemIds.value = enriched.map((r) => r.id);
            error.value = null;
            return;
          }
        } catch (fixtureErr) {
          console.error('[useSupplierBoxDetail] dev fixture load failed:', fixtureErr);
        }
      }

      error.value = t('common.errorTryAgain');
      console.error('[useSupplierBoxDetail] fetch error:', err);
    } finally {
      if (!silent) loading.value = false;
      if (abortController === controller) abortController = null;
    }
  }

  function loadInitial() {
    return fetchDetail({ silent: false });
  }

  // refresh(done)         → pull-to-refresh (F7 passes a `done` callback)
  // refresh({ silent })   → the poll / visibility path (no spinner, non-clobber)
  async function refresh(arg) {
    const silent = !!(arg && typeof arg === 'object' && arg.silent === true);
    const done = typeof arg === 'function' ? arg : null;
    try {
      await fetchDetail({ silent });
    } finally {
      if (done) done();
    }
  }

  // ── Writes: optimistic annotation with revert-on-error ──────────────────────
  // Flip the ORM signal BEFORE the request; on a SUPPLIER_WRITE_FROZEN rejection
  // (409 — gate closed underneath) revert + lock locally + toast the plain-language
  // reason + silently refetch so the now-frozen truth re-renders; on a transport
  // error revert + generic toast. Item-id-scoped, so it is naturally single-box.
  async function runWrite({ id, buildOptimistic, request, successKey }) {
    const item = itemRepo.find(id);
    if (!item) return;

    // Belt-and-braces: the card disables frozen controls, but never fire a write
    // the gate will reject anyway. Surface the reason like the tappable lock chip.
    if (item.frozen) {
      showToast(t(frozenReasonI18nKey(item.frozenReason)));
      return;
    }

    // Re-entrancy guard (H2): a rapid re-tap on the SAME item while its write is
    // still in flight is ignored — otherwise the `original` snapshot below would
    // capture the FIRST call's optimistic row (corrupting the revert baseline) and
    // last-RESPONSE-wins could override last-INTENT. The next tap works once this
    // one settles. (Frozen re-taps still fall through to the toast above.)
    if (pendingWriteIds.has(id)) return;
    pendingWriteIds.add(id);

    // Build the optimistic payload AFTER the find-guard so it can never be
    // derived from a since-flushed (undefined) row (e.g. a poll flush racing a tap).
    const original = { signal: item.signal, reservedQty: item.reservedQty };
    itemRepo.save({ id, ...buildOptimistic(item) });
    pendingWrites += 1;

    let frozenRejection = false;
    try {
      const optimisticRow = itemRepo.find(id);
      const updated = await request(optimisticRow);
      if (updated) itemRepo.save(reattachBoxContext(updated, optimisticRow));
      if (successKey) showToast(t(successKey));
    } catch (err) {
      itemRepo.save({ id, ...original });
      if (err?.code === SUPPLIER_WRITE_FROZEN) {
        frozenRejection = true;
        // The gate closed underneath us — this item is frozen server-side now.
        // Lock it locally (disable its controls + surface the «خلاص» chip) so a
        // re-tap can't re-fire the same rejection, even if the reconcile refetch
        // below fails and never lands server truth. A successful refetch simply
        // replaces this with the authoritative row.
        itemRepo.save({ id, frozen: true, frozenReason: err?.frozenReason ?? null });
        showToast(t(frozenReasonI18nKey(err?.frozenReason)));
      } else {
        showError(t('common.errorTryAgain'));
      }
    } finally {
      pendingWrites -= 1;
      pendingWriteIds.delete(id);
    }

    // Re-render the now-frozen server truth AFTER pendingWrites is cleared, so
    // fetchDetail's non-clobber guard doesn't skip it. Awaited so the settled
    // state is deterministic (matters for tests + rapid re-taps). Gated on
    // `disposed`: if the page tore down mid-write, skip the refetch entirely —
    // no post-unmount GET, no store-write into a box the page no longer owns.
    if (frozenRejection && !disposed) await refresh({ silent: true });
  }

  function reserve(id) {
    return runWrite({
      id,
      buildOptimistic: (item) => ({ signal: SupplierSignal.RESERVED, reservedQty: item.qty ?? 0 }),
      request: (item) => reserveItemRequest(item), // v1 always full qty (body reservedQty = item.qty)
      successKey: 'supplier.dailyOrders.toasts.reserved',
    });
  }

  function markOos(id) {
    return runWrite({
      id,
      buildOptimistic: () => ({ signal: SupplierSignal.OOS_FLAGGED, reservedQty: 0 }),
      request: (item) => markOosRequest(item),
      successKey: 'supplier.dailyOrders.toasts.oos',
    });
  }

  function clear(id) {
    return runWrite({
      id,
      buildOptimistic: () => ({ signal: null, reservedQty: 0 }),
      request: (item) => clearSignalRequest(item),
      successKey: 'supplier.dailyOrders.toasts.cleared',
    });
  }

  // ── Live-refresh (D4): 30s backstop + visibilitychange, armed ONLY while THIS
  //    box is IN_COLLECTION. Silent refetch (non-clobber). Single-box — no more
  //    cross-box Set iteration. ────────────────────────────────────────────────
  const pollArmed = computed(() => Number(box.value?.lifecycleState) === BOX_STATE_IN_COLLECTION);

  let pollTimer = null;
  let disposed = false;

  function startPolling() {
    stopPolling();
    if (disposed) return;
    pollTimer = setInterval(() => {
      if (pollArmed.value && pendingWrites === 0) refresh({ silent: true });
    }, POLL_INTERVAL_MS);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function onVisibilityChange() {
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible' &&
      pollArmed.value &&
      pendingWrites === 0
    ) {
      refresh({ silent: true });
    }
  }

  // The pollArmed watch is the SOLE arming authority (start/stop stay internal so
  // a caller can't fight it into a silently-disarmed state). immediate so a cold
  // load that's already IN_COLLECTION starts polling without waiting for a flip.
  const stopArmWatch = watch(
    pollArmed,
    (armed) => {
      if (disposed) return;
      if (armed) startPolling();
      else stopPolling();
    },
    { immediate: true },
  );

  const scope = getCurrentScope();
  if (scope && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  function dispose() {
    disposed = true;
    stopArmWatch(); // stop the watch FIRST so a late refetch can't re-arm the timer
    stopPolling();
    if (abortController) abortController.abort();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  }

  // Auto-clean when the owning page's scope tears down; also RETURNED so callers
  // can dispose explicitly. Two caveats a consumer MUST heed:
  //   • M1: outside a component scope (e.g. a test, a Pinia store), onScopeDispose
  //     is NOT registered, so the 30s timer (and the visibility listener, itself
  //     gated on `scope`) will leak unless the caller invokes dispose() manually —
  //     the unit tests do exactly this in afterEach.
  //   • H1 / keepAlive: framework7-vue SHORT-CIRCUITS Vue unmount for a keepAlive
  //     page (removePage returns before setPages), so onScopeDispose would NEVER
  //     fire for such a route. The detail page (P4-A) MUST call dispose() on
  //     @page:beforeremove as a belt-and-braces F7-native teardown — mirror
  //     OrderBoxTotalsPage.vue's onPageBeforeRemove. Do NOT set keepAlive on
  //     /suppliers/box/:boxId/ without wiring that hook.
  if (scope) onScopeDispose(dispose);

  return {
    // reactive data
    box,
    items,
    loading,
    error,
    // reads
    loadInitial,
    refresh,
    // writes
    reserve,
    markOos,
    clear,
    // summary
    totalCount,
    reservedCount,
    oosCount,
    totalAmount,
    // polling — the pollArmed watch self-arms; expose read-only state + teardown
    // only (start/stop stay internal so nothing can desync the arming authority).
    pollArmed,
    dispose,
  };
}
